import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { formatPercent, formatMinutesAsTime } from '@/utils/analyticsHelpers';
import { useJobCategoryEfficiency } from '@/hooks/kpi/useJobCategoryEfficiency';
import { useJobCategoryTechEfficiency } from '@/hooks/kpi/useJobCategoryTechEfficiency';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface JobCategoryPerformanceProps {
  shopId: string;
  startDate: string;
  endDate: string;
  shopTimezone: string;
}

interface CategoryStats {
  category: string;
  totalBookMinutes: number;
  totalActualMinutes: number;
  ticketCount: number;
  efficiency: number | null;
}

interface TechStats {
  techId: string;
  techName: string;
  totalBookMinutes: number;
  totalActualMinutes: number;
  ticketCount: number;
  efficiency: number | null;
}

export function JobCategoryPerformance({ shopId, startDate, endDate, shopTimezone }: JobCategoryPerformanceProps) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // K-T-10: top-view aggregation sourced from canonical
  // `kpi_efficiency_by_job_category` RPC.
  const { data: rawCategoryRows, isLoading: loading } = useJobCategoryEfficiency({
    shopId,
    startDate,
    endDate,
    shopTimezone,
  });

  // K-T-12: per-tech drill-down within the expanded category sourced
  // from canonical `kpi_efficiency_by_job_category_tech` RPC. Replaces
  // the prior local jobs + tickets + ticket_jobs + technicians queries
  // and the inline 3-level book-time fallback chain. Behavior change:
  // techs with fewer than 3 qualifying ticket_jobs (book>0 AND
  // actual>0) now render an "—" placeholder instead of a noisy
  // single-row ratio, matching K-T-6..K-T-11 convention.
  const { data: rawTechRows, isLoading: loadingDrilldown } = useJobCategoryTechEfficiency({
    shopId,
    startDate,
    endDate,
    shopTimezone,
    category: expandedCategory,
  });

  const categoryStats: CategoryStats[] = (rawCategoryRows ?? [])
    .map((r) => ({
      category: r.category,
      totalBookMinutes: r.total_book_minutes,
      totalActualMinutes: r.total_actual_minutes,
      ticketCount: r.ticket_count,
      // Canonical RPC returns ratio as a fraction (e.g. 0.92);
      // the panel displays as a percent-rounded number to match the
      // pre-K-T-10 `calculateEfficiency` output convention.
      efficiency: r.gated || r.ratio === null
        ? null
        : Math.round(r.ratio * 100),
    }))
    .sort((a, b) => (b.efficiency ?? 0) - (a.efficiency ?? 0));

  const techDrilldown: TechStats[] = (rawTechRows ?? [])
    .map((r) => ({
      techId: r.tech_id,
      techName: r.tech_name,
      totalBookMinutes: r.total_book_minutes,
      totalActualMinutes: r.total_actual_minutes,
      ticketCount: r.ticket_count,
      efficiency: r.gated || r.ratio === null
        ? null
        : Math.round(r.ratio * 100),
    }))
    .sort((a, b) => (b.efficiency || 0) - (a.efficiency || 0));

  function handleCategoryClick(category: string) {
    setExpandedCategory(expandedCategory === category ? null : category);
  }

  function getEfficiencyColor(efficiency: number | null): string {
    if (efficiency === null) return '#6b7280'; // Gray
    if (efficiency >= 100) return '#22c55e'; // Bright green
    if (efficiency >= 80) return '#eab308'; // Bright yellow
    return '#ef4444'; // Bright red
  }

  const chartData = categoryStats.slice(0, 10).map(cat => ({
    name: cat.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    efficiency: cat.efficiency || 0,
  }));

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Job Category Performance</CardTitle>
        <CardDescription>
          Efficiency by job category (book time ÷ actual time × 100)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {categoryStats.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No completed jobs in this date range</p>
        ) : (
          <>
            {/* Bar Chart */}
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis type="number" domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value: number) => [`${value}%`, 'Efficiency']} />
                  <Bar dataKey="efficiency" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={getEfficiencyColor(entry.efficiency)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Jobs</TableHead>
                  <TableHead className="text-right">Book Time</TableHead>
                  <TableHead className="text-right">Actual Time</TableHead>
                  <TableHead className="text-right">Efficiency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categoryStats.map((cat) => (
                  <>
                    <TableRow 
                      key={cat.category}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleCategoryClick(cat.category)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {expandedCategory === cat.category ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          {cat.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{cat.ticketCount}</TableCell>
                      <TableCell className="text-right">{formatMinutesAsTime(cat.totalBookMinutes)}</TableCell>
                      <TableCell className="text-right">{formatMinutesAsTime(cat.totalActualMinutes)}</TableCell>
                      <TableCell className="text-right">
                        <Badge 
                          variant={cat.efficiency && cat.efficiency >= 100 ? 'default' : 'secondary'}
                          className={cat.efficiency && cat.efficiency >= 100 ? 'bg-green-500/20 text-green-700 dark:text-green-300' : ''}
                        >
                          {formatPercent(cat.efficiency)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    {expandedCategory === cat.category && (
                      <TableRow>
                        <TableCell colSpan={5} className="bg-muted/30 p-4">
                          {loadingDrilldown ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                          ) : techDrilldown.length === 0 ? (
                            <p className="text-muted-foreground text-sm text-center">No technician data</p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Technician</TableHead>
                                  <TableHead className="text-right">Jobs</TableHead>
                                  <TableHead className="text-right">Book</TableHead>
                                  <TableHead className="text-right">Actual</TableHead>
                                  <TableHead className="text-right">Efficiency</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {techDrilldown.map((tech) => (
                                  <TableRow key={tech.techId}>
                                    <TableCell>{tech.techName}</TableCell>
                                    <TableCell className="text-right">{tech.ticketCount}</TableCell>
                                    <TableCell className="text-right">{formatMinutesAsTime(tech.totalBookMinutes)}</TableCell>
                                    <TableCell className="text-right">{formatMinutesAsTime(tech.totalActualMinutes)}</TableCell>
                                    <TableCell className="text-right">
                                      <Badge variant="outline">{formatPercent(tech.efficiency)}</Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}
